export function hazardsCount(standard: {
  significantHazards?: { hazards?: unknown[] } | null
}): number | null {
  if (standard.significantHazards == null) {
    return null
  }
  return standard.significantHazards.hazards?.length ?? 0
}

/** True if hazards, information-for-use, or RRMs have been set (extraction or manual). */
export function hasHazardsOrIfuData(standard: {
  significantHazards?: unknown | null
  informationForUseRequirements?: unknown | null
  riskReductionMeasures?: unknown | null
}): boolean {
  return (
    standard.significantHazards != null ||
    standard.informationForUseRequirements != null ||
    standard.riskReductionMeasures != null
  )
}

export function informationForUseClauseCount(standard: {
  informationForUseRequirements?: unknown
}): number | null {
  if (standard.informationForUseRequirements == null) {
    return null
  }
  const info = standard.informationForUseRequirements as {
    clauses?: unknown[]
  }
  return Array.isArray(info?.clauses) ? info.clauses.length : 0
}

/** Extraction saved an empty hazards list (section absent in PDF), not the same as null. */
export function isSignificantHazardsConfirmedEmpty(standard: {
  significantHazards?: { hazards?: unknown[] } | null
}): boolean {
  if (standard.significantHazards == null) {
    return false
  }
  return (standard.significantHazards.hazards?.length ?? 0) === 0
}

export function isRiskReductionMeasuresConfirmedEmpty(standard: {
  riskReductionMeasures?: { measures?: unknown[] } | null
}): boolean {
  if (standard.riskReductionMeasures == null) {
    return false
  }
  return (standard.riskReductionMeasures.measures?.length ?? 0) === 0
}

export function isInformationForUseConfirmedEmpty(standard: {
  informationForUseRequirements?: unknown
}): boolean {
  if (standard.informationForUseRequirements == null) {
    return false
  }
  const info = standard.informationForUseRequirements as {
    clauses?: unknown[]
    noRequirements?: boolean
  }
  if (info.noRequirements === true) {
    return true
  }
  return Array.isArray(info.clauses) && info.clauses.length === 0
}

export type ExtractedContentState =
  | 'noPdf'
  | 'notExtracted'
  | 'empty'
  | 'filled'

export function standardHasPdf(standard: {
  fileName: string | null
  filePathInBucket: string | null
}): boolean {
  return Boolean(standard.fileName) || Boolean(standard.filePathInBucket)
}

function extractedStateWhenSectionUnset(hasPdf: boolean): ExtractedContentState {
  if (hasPdf) {
    return 'notExtracted'
  }
  return 'noPdf'
}

export function getScopeExtractedState(standard: {
  scope: string | null
  fileName: string | null
  filePathInBucket: string | null
}): ExtractedContentState {
  const trimmedScope = standard.scope?.trim() ?? ''
  if (trimmedScope.length > 0) {
    return 'filled'
  }
  if (standard.scope != null) {
    return 'empty'
  }
  return extractedStateWhenSectionUnset(standardHasPdf(standard))
}

export function getSignificantHazardsExtractedState(standard: {
  significantHazards?: { hazards?: unknown[] } | null
  fileName: string | null
  filePathInBucket: string | null
}): ExtractedContentState {
  if (standard.significantHazards != null) {
    if (isSignificantHazardsConfirmedEmpty(standard)) {
      return 'empty'
    }
    return 'filled'
  }
  return extractedStateWhenSectionUnset(standardHasPdf(standard))
}

export function getInformationForUseExtractedState(standard: {
  informationForUseRequirements?: unknown
  fileName: string | null
  filePathInBucket: string | null
}): ExtractedContentState {
  if (standard.informationForUseRequirements != null) {
    if (isInformationForUseConfirmedEmpty(standard)) {
      return 'empty'
    }
    return 'filled'
  }
  return extractedStateWhenSectionUnset(standardHasPdf(standard))
}

export function getRiskReductionMeasuresExtractedState(standard: {
  riskReductionMeasures?: { measures?: unknown[] } | null
  fileName: string | null
  filePathInBucket: string | null
}): ExtractedContentState {
  if (standard.riskReductionMeasures != null) {
    if (isRiskReductionMeasuresConfirmedEmpty(standard)) {
      return 'empty'
    }
    return 'filled'
  }
  return extractedStateWhenSectionUnset(standardHasPdf(standard))
}

export type ExtractedContentType =
  | 'scope'
  | 'significantHazards'
  | 'informationForUse'
  | 'riskReductionMeasures'

export function getExtractedContentTypeStates(standard: {
  scope: string | null
  significantHazards?: { hazards?: unknown[] } | null
  informationForUseRequirements?: unknown
  riskReductionMeasures?: { measures?: unknown[] } | null
  fileName: string | null
  filePathInBucket: string | null
}): Array<{ type: ExtractedContentType; state: ExtractedContentState }> {
  return [
    { type: 'scope', state: getScopeExtractedState(standard) },
    {
      type: 'significantHazards',
      state: getSignificantHazardsExtractedState(standard),
    },
    {
      type: 'informationForUse',
      state: getInformationForUseExtractedState(standard),
    },
    {
      type: 'riskReductionMeasures',
      state: getRiskReductionMeasuresExtractedState(standard),
    },
  ]
}
